/**
 * The in-flight assistant reply. Deltas accumulate in component state; the persisted message
 * becomes the source of truth once the turn ends.
 */
export interface YbotStream {
  conversationId: string | undefined;
  turnId: string | undefined;
  text: string;

  /** The prompt we asked, rendered until the server's copy arrives. Empty for adopted turns. */
  prompt: string;

  /** When that prompt was sent, so the optimistic copy can retire once the transcript catches up. */
  postedAt: number;

  /** Open tool calls, oldest first - the only sign of life while the turn produces no text. */
  tools: YbotRunningTool[];
}

export interface YbotRunningTool {
  id: string;
  name: string;
  /** What the call is doing, in the model's own words, or undefined when it did not say. */
  label: string | undefined;

  /** Set when a subagent made this call. The server sends every call; what to show is decided here. */
  parentId: string | undefined;
}

/** The agent's own calls; a subagent's tools hide behind the subagent's chip (`parentId` set). */
export const directTools = (tools: YbotRunningTool[]): YbotRunningTool[] =>
  tools.filter(tool => tool.parentId === undefined);

export const emptyYbotStream: YbotStream = {
  conversationId: undefined,
  turnId: undefined,
  text: '',
  prompt: '',
  postedAt: 0,
  tools: [],
};

export type YbotStreamAction =
  | { type: 'watch'; conversationId: string; turnId: string; prompt?: string; postedAt?: number }
  | { type: 'delta'; conversationId: string; turnId: string; text: string }
  | { type: 'ended'; conversationId: string; turnId: string }
  | {
      type: 'toolStarted';
      conversationId: string;
      turnId: string;
      toolId: string;
      name: string;
      label: string | undefined;
      parentId: string | undefined;
    }
  | { type: 'toolEnded'; conversationId: string; turnId: string; toolId: string }
  | { type: 'reset' };

const isCurrent = (state: YbotStream, conversationId: string, turnId: string): boolean =>
  state.conversationId === conversationId && state.turnId === turnId;

/**
 * Events match both the conversation and the turn: a superseded worker cannot be cancelled, so its
 * stray deltas are discarded, and the conversation alone fails after a reset.
 */
export const ybotStreamReducer = (state: YbotStream, action: YbotStreamAction): YbotStream => {
  switch (action.type) {
    case 'watch':
      // Idempotent: re-announcing the watched turn must not throw away what has arrived so far.
      return isCurrent(state, action.conversationId, action.turnId)
        ? state
        : {
            conversationId: action.conversationId,
            turnId: action.turnId,
            text: '',
            prompt: action.prompt ?? '',
            postedAt: action.postedAt ?? 0,
            tools: [],
          };

    case 'delta':
      return isCurrent(state, action.conversationId, action.turnId)
        ? { ...state, text: state.text + action.text }
        : state;

    case 'toolStarted':
      // The CLI can announce the same call twice, so key by id rather than append blindly.
      return isCurrent(state, action.conversationId, action.turnId) &&
        !state.tools.some(tool => tool.id === action.toolId)
        ? {
            ...state,
            tools: [
              ...state.tools,
              {
                id: action.toolId,
                name: action.name,
                label: action.label,
                parentId: action.parentId,
              },
            ],
          }
        : state;

    case 'toolEnded':
      return isCurrent(state, action.conversationId, action.turnId)
        ? { ...state, tools: state.tools.filter(tool => tool.id !== action.toolId) }
        : state;

    case 'ended':
      // A late ending from a turn that is no longer being shown must not clear the current bubble.
      return isCurrent(state, action.conversationId, action.turnId) ? emptyYbotStream : state;

    case 'reset':
      return state === emptyYbotStream ? state : emptyYbotStream;
  }
};

/** Elapsed seconds, or undefined for a missing or unparsable timestamp. */
export const turnElapsedSeconds = (
  turnStartedAt: string | null | undefined,
  now: number,
): number | undefined => {
  if (!turnStartedAt) {
    return undefined;
  }

  const started = Date.parse(turnStartedAt);
  if (Number.isNaN(started)) {
    return undefined;
  }

  return Math.max(0, Math.floor((now - started) / 1000));
};

/** Whether the turn outlived the server's own ceiling (from availability), so reset can be offered. */
export const isTurnOverdue = (
  turnStartedAt: string | null | undefined,
  turnTimeoutSeconds: number | undefined,
  now: number,
): boolean => {
  if (turnTimeoutSeconds === undefined) {
    return false;
  }

  const elapsed = turnElapsedSeconds(turnStartedAt, now);
  return elapsed !== undefined && elapsed > turnTimeoutSeconds;
};

export const formatElapsed = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};
